import { defineStore } from 'pinia';
import { useProductEditorStore } from '@/v2/modules/products/stores/product-editor/index';
import { marketplacesEnum } from '@/v2/utils/marketplaces';

export const useWooCommerceStore = defineStore('woocommerce', {
	state: () => ({
		data: {},
		variants: {},
	}),
	getters: {
		formValues(state) {
			const productEditorStore = useProductEditorStore();

			const formValues = {};

			productEditorStore.woocommerceLinkedMarketplaces.forEach((m) => {
				const linkedVariants = {};

				productEditorStore.data.variants.forEach((variant) => {
					const linkedVariant = this.data[m.id]?.linked_variants.find(
						(el) => el.id === variant.id,
					);

					if (!linkedVariant) return;

					// Use latest prices from main pricing form (linked_marketplaces_prices)
					const mainFormPrice = variant.linked_marketplaces_prices?.find(
						(p) => p.linked_marketplace_id === m.id,
					);

					linkedVariants[variant.id] = {
						title_ar: this.data[m.id]?.title_ar,
						description_ar: this.data[m.id]?.description_ar,
						...linkedVariant,
						selling_price: mainFormPrice?.selling_price ?? linkedVariant.selling_price,
						cost_price: mainFormPrice?.cost_price ?? linkedVariant.cost_price,
						discounted_price: mainFormPrice?.discounted_price ?? linkedVariant.discounted_price,
						extra_data: {
							...(linkedVariant.extra_data || {}),
							linker_categories: (this.data[m.id]?.linker_categories || []).map(
								(c) => (typeof c === 'object' ? c.id : c),
							),
							meta_data: this.data[m.id]?.meta_data || [],
						},
					};
				});

				formValues[m.id] = {
					new_product_images:
						(this.data[m.id]?.linked_variant_images || [])
							.filter((el) => el.new)
							.map((el) => ({
								product_image_id: el.id,
								image_url: el.image,
								linked_marketplace_id: m.id,
								marketplace_id: marketplacesEnum.WooCommerce,
							})),
					linked_product: {},
					linked_variant: linkedVariants,
				};
			});

			return formValues;
		},
	},
	actions: {
		async setData(marketplacesData) {
			const productEditorStore = useProductEditorStore();

			marketplacesData.forEach((woo) => {
				const linkedProduct = woo?.linked_product;
				const linkedVariants = woo?.linked_variant;
				const variants = Object.values(linkedVariants).map((el) => el[0]);

				let title_ar = '';
				let description_ar = '';

				title_ar =
					variants[0]?.translations?.find((item) => item.locale == 'ar')?.title ||
					linkedProduct?.translations?.find((item) => item.locale == 'ar')?.title ||
					productEditorStore.data.title_ar;
				description_ar =
					variants[0]?.translations?.find((item) => item.locale == 'ar')?.description ||
					linkedProduct?.translations?.find((item) => item.locale == 'ar')?.short_description ||
					productEditorStore.data.short_description_ar;

				// Extract extra_data from first variant
				const extraData = variants[0]?.extra_data || {};

				const data = {
					title_ar,
					description_ar,
					// The select runs with returnObject, so it needs the category objects.
					// extra_data.linker_categories holds bare ids, which rendered as raw
					// numbers until the page of options containing them happened to load.
					// LinkedProductsController enriches every marketplace with the objects.
					linker_categories: extraData.linker_categories_object || [],
					meta_data: extraData.meta_data || [],
					// Linked images if any, otherwise the main product images. Removing
					// this fallback was premature: nothing created the link rows for
					// WooCommerce, so the tab went empty instead of matching Salla.
					linked_variant_images: linkedProduct?.linked_product_images?.length
						? linkedProduct.linked_product_images.map((image) => ({
							...image,
							image: image.path,
						}))
						: (productEditorStore.data.product_images || []).map((image) => ({
							...image,
							id: image.id,
							image: image.path,
							new: true,
						})),
					// Variants with price/quantity
					linked_variants: variants.map((variant) => {
						const productVariant = productEditorStore.data.variants.find(
							(el) => el.id === variant.variant_id,
						);

						// Get per-market price from main pricing form
						const mainFormPrice = productVariant?.linked_marketplaces_prices?.find(
							(p) => p.linked_marketplace_id === woo.linked_marketplace_id,
						);

						return {
							id: variant.variant_id,
							sku: variant.sku,
							barcode: variant.barcode || productVariant?.barcode,
							selling_price: variant.selling_price || mainFormPrice?.selling_price || productVariant?.price || '',
							cost_price: variant.cost_price || mainFormPrice?.cost_price || '',
							discounted_price: variant.discounted_price || mainFormPrice?.discounted_price || '',
							quantity: variant.quantity ?? productVariant?.quantity ?? 0,
						};
					}),
				};

				this.variants[woo.linked_marketplace_id] = variants.map((v) => ({
					...v,
					marketplace_status: v.marketplace_status || (v.status === 'LISTED' ? 'BUYABLE' : 'UNLISTED'),
				}));
				this.data[woo.linked_marketplace_id] = data;
			});
		},
		async submit() {},
	},
});
